/**
 * trialStatus — pure derivation for the user's trial state.
 *
 * Derives everything the trial UI needs from users/{uid}.trial (written by
 * redeemAccessCode / deductTrialCredits / enforceTrialExpiry): days left, a
 * friendly end date, remaining credit, and whether the trial is expiring soon
 * or already over. No Firebase imports — unit-testable without the SDK; the
 * React hook lives in useTrialStatus.ts.
 *
 * Trial terms recap (see functions/src/accessCodes.ts TRIAL_TIERS):
 *   Tier A: $1,000 credit, 4 weeks.  Tier B: $300 credit, 2 weeks.
 * Trial accounts can never top up credits — at (or near) expiry the user is
 * directed to the subscription page to hire their prest0n (self-serve since
 * task #59; no personal contact email is ever printed — owner rule 2026-08-16).
 */

import { expiryToMillis, TrialExpiryValue } from '../components/GlobalChatSystem/entitlementBypass';
import { TRIAL_CONVERSION_CHARGE_WINDOW_MS } from '../types/trialConversion';

/** Days-left threshold at which we treat the trial as "expiring soon". */
export const TRIAL_EXPIRING_SOON_DAYS = 3;

/**
 * Graceful in-chat response for expired trials (owner directive 2026-07-31):
 * the user must not be able to keep chatting, and the reply should point them
 * at self-serve activation instead of a top-up CTA.
 */
// copy: system:trial_ended
export const TRIAL_ENDED_CHAT_MESSAGE =
  `🔒 your trial has ended — thanks for exploring Prest0n! chat is paused for now, but your workspace and everything Prest0n has learned are safe. hire your Prest0n on the subscription page and everything picks up right where it left off.`;

export interface TrialDocShape {
  active?: boolean | null;
  tier?: 'a' | 'b' | string | null;
  creditAmountCents?: number | null;
  creditRemainingCents?: number | null;
  expiresAt?: TrialExpiryValue;
}

/**
 * Minimal shape of users/{uid}/subscription/subscription for trial gating.
 * A user counts as a trial user ONLY when their CURRENT subscription says so
 * (written by redeemAccessCode): is_trial === true or plan === 'trial'.
 *
 * This is the load-bearing gate: early activated accounts (e.g. Fernando's
 * enterprise tenant) still carry a legacy users/{uid}.trial field with a
 * long-past expiry — keying trial UI off that field alone showed them a
 * "your trial has ended" banner. Their subscription doc says enterprise/
 * active, so the gate excludes them; converting a trial to a full plan
 * rewrites the subscription doc and clears the trial UI the same way.
 */
export interface TrialSubscriptionShape {
  is_trial?: boolean | null;
  plan?: string | null;
  status?: string | null;
  is_client_free?: boolean | null;
  trial_credit_cents?: number | null;
  trial_expires_at?: TrialExpiryValue;
  /** Server-mirrored exempt-organization flag (task #76) — display only. */
  trial_exempt?: boolean | null;
  /**
   * The trial's conversion (2026-10-20, docs/PLAN-trial-card-on-file-20261020.md), written only by the server: its
   * state word, whether the trial needed a card before any work, when the plan starts, and the card's brand and last
   * four. The person's own document carries the card; an organization's members read the mirror, which never does.
   */
  trial_conversion?: {
    state?: string | null;
    required?: boolean | null;
    charge_at_ms?: number | null;
    card_brand?: string | null;
    card_last4?: string | null;
  } | null;
}

/** The trial's conversion as every trial surface reads it (the banner, the app's card step, /subscription). */
export interface TrialConversionView {
  /** The state word (functions/src/billing/trialConversionVocabulary.ts), e.g. awaiting_card, armed, off. */
  state: string;
  /** This trial needed a card before any work (a trial from before cards did not). */
  required: boolean;
  /** When the plan starts, if the server has a date. */
  chargeAtMs: number | null;
  /** The card, where the reader's own document carries it; never through an organization's member mirror. */
  card: { brand: string; last4: string } | null;
}

/** The conversion map off a subscription document (or the mirror's three facts), or null when it has none. */
export function parseTrialConversion(raw: TrialSubscriptionShape['trial_conversion'] | unknown): TrialConversionView | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.state !== 'string' || !r.state) return null;
  const at = r.charge_at_ms;
  const last4 = typeof r.card_last4 === 'string' && r.card_last4 ? r.card_last4 : null;
  return {
    state: r.state,
    required: r.required === true,
    chargeAtMs: typeof at === 'number' && Number.isFinite(at) ? at : null,
    card: last4 ? { brand: typeof r.card_brand === 'string' && r.card_brand ? r.card_brand : 'card', last4 } : null,
  };
}

/** A trial that waits for its card before any work (the server's one blocking state). */
export function trialAwaitsCard(conversion: TrialConversionView | null | undefined): boolean {
  return !!conversion && conversion.required && conversion.state === 'awaiting_card';
}

/**
 * Where the server keeps a trial open past its end date (functions/src/prest0nSession/paymentGate.ts, the same two
 * rules): a trial waiting for its card never reads as ended — its clock starts when the card is added — and an armed
 * one keeps working for TRIAL_CONVERSION_CHARGE_WINDOW_MS past its end while its first charge resolves. Without this
 * the page would lock the chat as "your trial has ended" while the server is still taking work.
 */
export function trialHeldOpen(conversion: TrialConversionView | null | undefined, expiresAtMs: number | null, nowMs: number): boolean {
  if (trialAwaitsCard(conversion)) return true;
  if (!conversion || (conversion.state !== 'armed' && conversion.state !== 'charging')) return false;
  const end = Math.max(expiresAtMs ?? 0, conversion.chargeAtMs ?? 0);
  return end > 0 && nowMs < end + TRIAL_CONVERSION_CHARGE_WINDOW_MS;
}

export function isTrialSubscription(sub: TrialSubscriptionShape | null | undefined): boolean {
  // Client-free accounts (Prest0 absorbs cost — internal/testing/comped) are
  // never trial-gated, even if a stray trial flag exists on the doc.
  if (sub?.is_client_free === true) return false;
  return sub?.is_trial === true || sub?.plan === 'trial';
}

export interface TrialStatus {
  /** True until the first snapshot has arrived (or auth resolves signed-out). */
  isLoading: boolean;
  /** True if this user ever redeemed a trial (the trial field exists). */
  isTrialUser: boolean;
  /** True while the trial is active and not past its end date. */
  isActive: boolean;
  /** True once the trial is over (flagged inactive server-side or past end date). */
  isExpired: boolean;
  /** True when active but ending within TRIAL_EXPIRING_SOON_DAYS days. */
  isExpiringSoon: boolean;
  tier: 'a' | 'b' | null;
  creditAmountCents: number;
  creditRemainingCents: number;
  /** Whole days until the end date (ceil), 0 once past. Null if unknown. */
  daysRemaining: number | null;
  /** Friendly end date, e.g. "August 28". Null if unknown. */
  endsOn: string | null;
  expiresAtMs: number | null;
  /**
   * The workspace's LIVE wallet balance in pr0 (users/{uid}/wallet/summary
   * olv_balance) — the one number every trial surface displays, so the
   * banner always lines up with the wallet page (task #76, 2026-08-18
   * owner directive). Null until the wallet snapshot lands.
   */
  liveWalletWu: number | null;
  /** Exempt organization (task #76): hide test-mode chrome, never blocked. */
  trialExempt: boolean;
  /** The trial's conversion to a plan when it ends (2026-10-20), or null for a trial from before cards. */
  conversion: TrialConversionView | null;
}

export const NO_TRIAL: TrialStatus = {
  isLoading: false,
  isTrialUser: false,
  isActive: false,
  isExpired: false,
  isExpiringSoon: false,
  tier: null,
  creditAmountCents: 0,
  creditRemainingCents: 0,
  daysRemaining: null,
  endsOn: null,
  expiresAtMs: null,
  liveWalletWu: null,
  trialExempt: false,
  conversion: null,
};

/**
 * WORKSPACE trial facts as stamped on accounts/{id}/wallet/displaySummary by
 * ensureAccountWalletDisplayMirror (functions/src/lib/accountExpense.ts) —
 * the one member-readable doc the wallet page already trusts for the acting
 * account's balance (owner report 2026-08-17: the trial banner must reflect
 * the ACTIVE workspace's numbers, exactly like the wallet page).
 */
export interface WorkspaceTrialFacts {
  /** users/{ownerUid}/subscription/subscription exists for the workspace owner. */
  subscriptionDocExists: boolean;
  subscriptionPlan: string | null;
  subscriptionIsTrial: boolean;
  subscriptionIsClientFree: boolean;
  trialActive: boolean | null;
  trialTier: string | null;
  /** Redemption-time credit SEED in cents (never decrements). */
  trialCreditCents: number | null;
  trialExpiresAtMs: number | null;
  /**
   * `entitlement_source` on the mirror — stamped ONLY by the server's
   * read-time grant overlay (functions/src/rbac/internalEntitlement.ts, the
   * code-pinned internal organization). A granted workspace IS subscribed,
   * whoever's wallet pays for it, so no trial chrome and no trial lock may
   * ever render inside it. Optional because pre-grant mirrors never carried it.
   */
  entitlementSource?: string | null;
  /** The trial's conversion, from the mirror's three facts (never the card: every member reads the mirror). */
  conversion?: TrialConversionView | null;
}

/**
 * The grant marker off a raw displaySummary doc, independent of whether the
 * trial-facts stamp is present: a non-empty string is a grant, anything else
 * is "no grant". Read separately from parseWorkspaceTrialFacts so a mirror
 * that predates the facts stamp (which parses to null) still cannot hide a
 * grant behind a null.
 */
export function parseWorkspaceEntitlementSource(
  data: Record<string, unknown> | null | undefined,
): string | null {
  if (!data || typeof data !== 'object') return null;
  const raw = (data as { entitlement_source?: unknown }).entitlement_source;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/** A granted workspace is never trial-gated: banner hidden, chat never locked. */
export function workspaceGrantWaivesTrial(entitlementSource: string | null | undefined): boolean {
  return typeof entitlementSource === 'string' && entitlementSource.trim().length > 0;
}

export type ForeignWorkspaceTrialSource = 'loading' | 'no-trial' | 'native' | 'workspace';

export interface ForeignWorkspaceMirrorFacts {
  status: 'idle' | 'loading' | 'ready' | 'missing' | 'error';
  /** The mirror's paying wallet is the signed-in member's own (payer_uid === uid). */
  payerIsMe: boolean;
  entitlementSource: string | null;
}

/**
 * Which trial state a FOREIGN workspace shows — the one decision the hook
 * makes once the display mirror has answered, kept pure so its ORDER is
 * testable:
 *
 *   1. mirror still loading            → loading (never a lock, never a banner);
 *   2. the workspace carries a grant   → NO_TRIAL — decided BEFORE the payer
 *      rule. INCIDENT 2026-09-15: the internal organization bills its owner's
 *      PERSONAL wallet (payer_uid === owner uid), so the payer shortcut handed
 *      back the owner's expired personal trial and every founders' thread
 *      showed "🔒 your trial has ended" with sends blocked — while the
 *      server was billing the turns normally under the grant;
 *   3. my own wallet pays this workspace → native (my live feeds ARE its numbers);
 *   4. mirror missing / unreadable      → NO_TRIAL (a native trial never leaks);
 *   5. otherwise                        → the workspace's own facts.
 */
export function decideForeignWorkspaceTrialSource(
  mirror: ForeignWorkspaceMirrorFacts,
): ForeignWorkspaceTrialSource {
  if (mirror.status === 'idle' || mirror.status === 'loading') return 'loading';
  if (mirror.status === 'ready' && workspaceGrantWaivesTrial(mirror.entitlementSource)) return 'no-trial';
  if (mirror.status === 'ready' && mirror.payerIsMe) return 'native';
  if (mirror.status !== 'ready') return 'no-trial';
  return 'workspace';
}

/**
 * Parse the trial-facts fields off a raw displaySummary doc. Returns null for
 * mirrors that predate the trial-facts stamp (no subscription_doc_exists /
 * trial_synced_at marker) — callers fail toward "no trial banner" until the
 * next ensure refresh stamps them.
 */
export function parseWorkspaceTrialFacts(
  data: Record<string, unknown> | null | undefined,
): WorkspaceTrialFacts | null {
  if (!data || typeof data !== 'object') return null;
  if (!('subscription_doc_exists' in data) && !('trial_synced_at' in data)) return null;
  const num = (v: unknown): number | null =>
    typeof v === 'number' && isFinite(v) ? v : null;
  return {
    subscriptionDocExists: data.subscription_doc_exists === true,
    subscriptionPlan: typeof data.subscription_plan === 'string' ? data.subscription_plan : null,
    subscriptionIsTrial: data.subscription_is_trial === true,
    subscriptionIsClientFree: data.subscription_is_client_free === true,
    trialActive: typeof data.trial_active === 'boolean' ? data.trial_active : null,
    trialTier: typeof data.trial_tier === 'string' ? data.trial_tier : null,
    trialCreditCents: num(data.trial_credit_cents),
    trialExpiresAtMs: num(data.trial_expires_at_ms),
    entitlementSource: parseWorkspaceEntitlementSource(data),
    conversion: parseTrialConversion({
      state: data.trial_conversion_state,
      required: data.trial_conversion_required,
      charge_at_ms: data.trial_conversion_charge_at_ms,
    }),
  };
}

/**
 * Derive the ACTIVE WORKSPACE's trial status from the display-mirror facts +
 * the mirror's live paying-wallet balance (converted to cents by the caller
 * at the same rate the wallet page uses). Same gating/derivation rules as
 * deriveTrialStatus — including the Fernando-class subscription gate and the
 * narrow trial-only-on-user-doc exception — so the banner can never disagree
 * with what the workspace owner's own surfaces would say.
 *
 * `facts === null` (mirror missing, unreadable, or predating the trial-facts
 * stamp) fails toward NO_TRIAL: in a foreign workspace the member's native
 * trial state must never leak into the banner, and a hidden banner is the
 * safe direction until the next workspace switch / org-facts bridge refresh
 * stamps the facts.
 */
export function deriveWorkspaceTrialStatus(
  facts: WorkspaceTrialFacts | null,
  liveBalanceCents: number | null,
  nowMs: number,
  liveWalletWu: number | null = null,
): TrialStatus {
  if (!facts) return { ...NO_TRIAL };
  // A granted workspace is subscribed by definition (belt: the overlay also
  // writes a non-trial plan, so the subscription gate below would agree —
  // this line keeps that true even if a mirror ever carries both).
  if (workspaceGrantWaivesTrial(facts.entitlementSource)) return { ...NO_TRIAL };
  const conversion = facts.conversion ?? null;
  const subscription: TrialSubscriptionShape | null = facts.subscriptionDocExists
    ? {
        is_trial: facts.subscriptionIsTrial === true,
        plan: facts.subscriptionPlan,
        is_client_free: facts.subscriptionIsClientFree === true,
        trial_conversion: conversion
          ? { state: conversion.state, required: conversion.required, charge_at_ms: conversion.chargeAtMs }
          : null,
      }
    : null;
  const hasTrialField =
    facts.trialActive !== null || facts.trialExpiresAtMs !== null || facts.trialCreditCents !== null;
  const trial: TrialDocShape | null = hasTrialField
    ? {
        active: facts.trialActive,
        tier: facts.trialTier,
        creditAmountCents: facts.trialCreditCents,
        // The seed is the static fallback; the live paying-wallet balance
        // overrides below (same policy as the native hook's wallet listener).
        creditRemainingCents: facts.trialCreditCents,
        expiresAt: facts.trialExpiresAtMs,
      }
    : null;
  const status = deriveTrialStatus(trial, subscription, nowMs);
  if (status.isTrialUser && liveBalanceCents !== null) {
    // ONE number everywhere (task #76, 2026-08-18 owner directive): trial
    // surfaces print the workspace's ACTUAL wallet pr0 — identical to the
    // wallet page — never a seed-clamped figure that drifts from it.
    return {
      ...status,
      creditRemainingCents: Math.max(0, Math.round(liveBalanceCents)),
      liveWalletWu: liveWalletWu !== null ? Math.max(0, liveWalletWu) : status.liveWalletWu,
    };
  }
  return {
    ...status,
    liveWalletWu: liveWalletWu !== null ? Math.max(0, liveWalletWu) : status.liveWalletWu,
  };
}

export function deriveTrialStatus(
  trial: TrialDocShape | null,
  subscription: TrialSubscriptionShape | null,
  nowMs: number,
): TrialStatus {
  // Gate on the CURRENT subscription — the legacy users/{uid}.trial field
  // alone must never mark someone as a trial user (see TrialSubscriptionShape).
  //
  // One narrow exception (2026-08-15 owner report: a trial account saw no
  // trial banner at all). An account whose trial lives ONLY in the user doc —
  // no subscription document exists yet — is genuinely on a trial and had no
  // status surface anywhere. The exception is deliberately narrow so it cannot
  // reopen the incident the gate was built for:
  //   · it requires the subscription doc to be ABSENT, and every activated
  //     account has one (that is what excluded the enterprise tenants), and
  //   · it requires the trial to be active AND not past its end date, so a
  //     stale legacy trial field can never resurrect trial UI.
  if (!isTrialSubscription(subscription)) {
    const trialOnlyOnUserDoc =
      subscription == null &&
      trial?.active === true &&
      (() => {
        const ms = expiryToMillis(trial?.expiresAt);
        return ms !== null && ms > nowMs;
      })();
    if (!trialOnlyOnUserDoc) {
      return { ...NO_TRIAL };
    }
  }

  // Trial subscription without the user-doc trial field (edge: doc read raced
  // redemption) — derive what we can from the subscription itself.
  if (!trial) {
    trial = {
      active: true,
      creditAmountCents: subscription?.trial_credit_cents ?? null,
      creditRemainingCents: subscription?.trial_credit_cents ?? null,
      expiresAt: subscription?.trial_expires_at,
    };
  }

  const expiresAtMs = expiryToMillis(trial.expiresAt);
  const conversion = parseTrialConversion(subscription?.trial_conversion);
  const pastEnd = expiresAtMs !== null && expiresAtMs <= nowMs && !trialHeldOpen(conversion, expiresAtMs, nowMs);
  const isActive = trial.active === true && !pastEnd;
  const isExpired = trial.active === false || pastEnd;
  const daysRemaining = expiresAtMs !== null
    ? Math.max(0, Math.ceil((expiresAtMs - nowMs) / (1000 * 60 * 60 * 24)))
    : null;
  const endsOn = expiresAtMs !== null
    ? new Date(expiresAtMs).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;

  return {
    isLoading: false,
    isTrialUser: true,
    isActive,
    isExpired,
    isExpiringSoon: isActive && daysRemaining !== null && daysRemaining <= TRIAL_EXPIRING_SOON_DAYS,
    tier: trial.tier === 'a' || trial.tier === 'b' ? trial.tier : null,
    creditAmountCents: typeof trial.creditAmountCents === 'number' ? trial.creditAmountCents : 0,
    creditRemainingCents: typeof trial.creditRemainingCents === 'number' ? trial.creditRemainingCents : 0,
    daysRemaining,
    endsOn,
    expiresAtMs,
    liveWalletWu: null,
    trialExempt: subscription?.trial_exempt === true,
    conversion,
  };
}
